import React from "react";
import {
  Trash2,
  File,
  FileText,
  FileSpreadsheet,
  Image,
  type LucideIcon,
} from "lucide-react";
import { formatFileSize, getFileIcon } from "../../lib/storage";

interface PendingAttachmentListProps {
  files: File[];
  onRemove: (index: number) => void;
}

const iconMap: Record<string, LucideIcon> = {
  File,
  FileText,
  FileSpreadsheet,
  Image,
};

/**
 * Shows files staged for upload in the add-task flow, before the task (and
 * so a task id to upload against) exists yet. Mirrors AttachmentList's
 * layout but works off plain `File` objects instead of `TaskAttachment`
 * records — no id, no storage URL, no rename, since nothing is persisted
 * yet. The files themselves upload once the task is created — see
 * useCreateTask's `pending_files` handling.
 */
const PendingAttachmentList: React.FC<PendingAttachmentListProps> = ({
  files,
  onRemove,
}) => {
  if (files.length === 0) {
    return null;
  }

  return (
    <div className="bg-warm-bg-elevated rounded-lg border border-warm-border overflow-hidden">
      {files.map((file, index) => {
        const iconName = getFileIcon(file.type);
        const IconComponent = iconMap[iconName] || File;

        return (
          <div
            key={`${file.name}-${index}`}
            className={`flex items-center gap-3 px-3 py-1.5 hover:bg-warm-border-subtle transition-colors group ${
              index !== files.length - 1 ? "border-b border-warm-border" : ""
            }`}
          >
            <IconComponent
              size={16}
              className="flex-shrink-0 text-warm-text-secondary"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-warm-text-primary truncate">
                {file.name}
                <span className="text-xs text-warm-text-tertiary ml-1.5">
                  ({formatFileSize(file.size)})
                </span>
              </p>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => onRemove(index)}
                aria-label="Remove file"
                className="p-1.5 text-warm-text-secondary hover:text-red-500 transition-colors rounded hover:bg-warm-border-subtle"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PendingAttachmentList;
